import cartLineItem from './cart-line-item';
import {
  lockScroll,
  unlockScroll,
  hideBackground,
  showBackground,
  markCartDrawerClosed,
} from '../utils/util-functions';
import { fetchProductsFromClerk, isRetryableClerkOutcome } from '../utils/clerk-recommendations';
import { createFocusTrap } from '../lib/focus-trap';

export default (node) => {
  const cartButton = document.querySelector('[data-main-cart-button]');
  const mobileCartButton = document.querySelector('[data-mobile-cart-button]');
  const cartDrawerSell = document.querySelector('[data-cart-drawer-sell]');
  const cartDrawerContent = node.querySelector('[data-cart-drawer-content]');
  const clerkKey = node.getAttribute('data-clerk-key');
  const clerkUpsellLimit = node.getAttribute('data-clerk-upsell-limit');
  const clerkRecommendationsUrl = node.getAttribute('data-clerk-recommendations-url');
  const cartDrawerOverlay = node.querySelector('[data-cart-drawer-overlay]');
  const closeCartDrawerButtons = node.querySelectorAll('[data-close-cart-drawer-button]');
  const moneyFormat = node
    .getAttribute('data-money-format')
    .replace('amount_with_dot_separator', 'amount');
  const selectSize = node.getAttribute('data-select-size');
  const addToCartText = node.getAttribute('data-add-to-cart-text');
  const loadingText = node.getAttribute('data-loading-text');

  let cartItems = node.querySelectorAll('[data-cart-item]');

  let firstTimeOpen = false;

  let upsellRequest = null;
  let upsellRefreshQueued = false;

  /**
   * @param {{refresh?: boolean}} [options] - `refresh` marks an in-flight load stale, for when
   *   the cart changed under it. A repeat open without it just shares the pending request.
   */
  const loadUpsellProducts = ({ refresh = false } = {}) => {
    if (upsellRequest) {
      if (refresh) upsellRefreshQueued = true;

      return upsellRequest;
    }

    upsellRequest = fetchProductsFromClerk(node.querySelectorAll('[data-cart-item]'), clerkRecommendationsUrl, clerkKey, clerkUpsellLimit, true, moneyFormat, selectSize, addToCartText, null, '[data-cart-drawer-sell-products]', '[data-cart-drawer-sell-products-mobile-embla]', '["Upsell - Cart drawer"]', loadingText)
      .then((outcome) => {
        // An outcome that would repeat itself on the same cart is latched; one that a later
        // attempt could do better on is left for the next open to retry.
        if (!isRetryableClerkOutcome(outcome)) firstTimeOpen = true;
      })
      .finally(() => {
        upsellRequest = null;

        if (!upsellRefreshQueued) return;

        upsellRefreshQueued = false;
        firstTimeOpen = false;
        loadUpsellProducts({ refresh: true });
      });

    return upsellRequest;
  };

  const openOrLoadUpsell = () => {
    if (firstTimeOpen) {
      openUpsellDrawer();
      return;
    }
    loadUpsellProducts();
  };

  // Focus management for the modal dialog (WCAG 2.4.3 / 2.1.2)
  let destroyFocusTrap = null;
  let lastTrigger = null;

  const handleEscape = (e) => {
    if (e.key === 'Escape') toggleCartDrawer();
  };

  const onDrawerOpen = (trigger) => {
    lastTrigger = trigger || lastTrigger || document.activeElement;
    if (destroyFocusTrap) destroyFocusTrap();
    destroyFocusTrap = createFocusTrap(cartDrawerContent || node);
    document.addEventListener('keydown', handleEscape);
    // Hide background content from assistive tech while the dialog is open.
    // Coordinated with the header menus so <main> stays hidden until the last
    // overlay closes.
    hideBackground('cart-drawer');
    if (cartDrawerContent) {
      cartDrawerContent.setAttribute('tabindex', '-1');
      cartDrawerContent.focus();
    }
  };

  const onDrawerClose = () => {
    if (destroyFocusTrap) {
      destroyFocusTrap();
      destroyFocusTrap = null;
    }
    document.removeEventListener('keydown', handleEscape);
    showBackground('cart-drawer');
    // Record this user-initiated close so an auto-open already in flight (e.g.
    // add-to-cart resolving after its request) can detect it and skip reopening.
    markCartDrawerClosed();
    if (lastTrigger) {
      lastTrigger.focus();
      lastTrigger = null;
    }
  };

  const toggleCartDrawer = (e) => {
    const isExpanded = node.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      closeUpsellDrawer();
    }
    node.setAttribute('aria-expanded', !isExpanded);

    // Is reversed because this is the state before the toggle
    if (isExpanded) {
      unlockScroll();
      onDrawerClose();
    } else {
      lockScroll();
      onDrawerOpen(e && e.currentTarget);

      // Only open upsell when opening the cart drawer and cart has items
      if (cartItems.length > 0) {
        openOrLoadUpsell();
      }
    }
  };

  const openUpsellDrawer = () => {
    // Never show the upsell while the cart drawer itself is closed. This can run
    // asynchronously (via the cart-drawer:open-upsell-drawer event after the Clerk
    // fetch); if the user closed the drawer in the meantime, that late callback
    // would otherwise slide a pointer-events panel on-screen over the (closed) page.
    if (node.getAttribute('aria-expanded') !== 'true') {
      return;
    }
    // check window width
    if (window.innerWidth < 990) {
      return;
    }
    if (cartDrawerSell) {
      cartDrawerSell.classList.add('translate-x-0');
      cartDrawerSell.classList.add('right-[830px]');
    }
  };

  const closeUpsellDrawer = () => {
    if (cartDrawerSell) {
      cartDrawerSell.classList.remove('translate-x-0');
      cartDrawerSell.classList.remove('right-[830px]');
    }
  };

  const updateShippingText = () => {
    const shippingText = node.querySelector('[data-shipping-text]');
    if (!shippingText) return;
    const showShippingText = shippingText.dataset.showShippingText;
    const freeShippingAmount = shippingText.dataset.freeShippingAmount;

    if (showShippingText === 'true') {
      shippingText.innerHTML = shippingText.innerHTML.replace(
        '{AMOUNT}',
        freeShippingAmount
      );
    }
  };

  const updateCartItems = () => {
    if (cartItems.length > 0) {
      cartItems.forEach((item) => {
        cartLineItem(item);
      });
    } else {
      closeUpsellDrawer();
    }
  };

  const init = () => {
    if (cartButton) {
      cartButton.addEventListener('click', toggleCartDrawer);
    }

    if (mobileCartButton) {
      mobileCartButton.addEventListener('click', toggleCartDrawer);
    }

    if (cartDrawerOverlay) {
      cartDrawerOverlay.addEventListener('click', toggleCartDrawer);
    }

    if (closeCartDrawerButtons) {
      closeCartDrawerButtons.forEach((button) => {
        button.addEventListener('click', toggleCartDrawer);
      });
    }

    updateShippingText();
    updateCartItems();

    document.addEventListener('cart:open', () => {
      // Ignore re-fires while already open, so onDrawerOpen doesn't overwrite the
      // stored trigger and break focus-return on close (WCAG 2.4.3).
      if (node.getAttribute('aria-expanded') === 'true') return;
      node.setAttribute('aria-expanded', 'true');
      lockScroll();
      onDrawerOpen(null);

      // Only open upsell if cart has items
      const currentCartItems = node.querySelectorAll('[data-cart-item]');
      if (currentCartItems.length > 0) {
        openOrLoadUpsell();
      }
    });

    document.addEventListener('cart-drawer:open-upsell-drawer', (element) => {
      const currentCartItems = node.querySelectorAll('[data-cart-item]');
      if (element.detail.open && currentCartItems.length > 0) {
        openUpsellDrawer();
      }
    });

    document.addEventListener('cart:update', () => {
      updateShippingText();
      cartItems = node.querySelectorAll('[data-cart-item]');
      updateCartItems();

      const closeCartDrawerButtons = node.querySelectorAll(
        '[data-close-cart-drawer-button]'
      );
      if (closeCartDrawerButtons) {
        closeCartDrawerButtons.forEach((button) => {
          button.addEventListener('click', toggleCartDrawer);
        });
      }

      if (cartItems.length > 0) {
        loadUpsellProducts({ refresh: true });
      }
    });
  };

  init();
};
